/**
 * @file SSODetailsTab.js
 * @description SSO Details tab component for the Client SSO Wizard.
 */

import React, { useState, useEffect } from 'react';
import TenantSSOStatusDropdown from '../../shared/asyncDropdowns/TenantSSOStatusDropdown';
import { CharacterLimitIndicator } from '../../helpers/CharacterLimitIndicator';
import style from '../sso.module.scss';
import SSODropdown from '../../shared/asyncDropdowns/SSODropdown';
import LoadingWrapper from '../../ui/loaders/LoadingWrapper';
import { useAuthContext } from '../../../store/Contexts';
import { TenantTiers } from '../../../utils/data/enums';
import { getText } from '../../../i18n';

const SSODetailsTab = ({
    data,
    onChange,
    isAddMode,
    onSSOSelection,
    isLoadingSSOSelection,
    onSave,
    isClientSSOReadOnly,
    shouldShowSharedWithAdditionalUsers
}) => {
    const [formData, setFormData] = useState({
        name: data.name || '',
        url: data.url || '',
        tenantSSOStatusId: data.tenantSSOStatusId || '',
        urlIsLocked: data.urlIsLocked || false,
        isAdditionalUserVisible: data.isAdditionalUserVisible || false,
    });

    useEffect(() => {
        setFormData({
            name: data.name || '',
            url: data.url || '',
            tenantSSOStatusId: data.tenantSSOStatusId || '',
            urlIsLocked: data.urlIsLocked || false,
            isAdditionalUserVisible: data.isAdditionalUserVisible || false,
        });
    }, [data]);

    const handleChange = e => {
        const { name, value } = e.target;
        setFormData(prevData => {
            const newData = {
                ...prevData,
                [name]: value,
            };
            onChange(newData);
            return newData;
        });
    };

    const handleStatusChange = value => {
        setFormData(prevData => {
            const newData = {
                ...prevData,
                tenantSSOStatusId: value,
            };
            onChange(newData);
            return newData;
        });
    };

    const handleSharedWithAdditionalUsersChange = event => {
        const checked = event.target.checked;
        setFormData(prevData => {
            const newData = {
                ...prevData,
                isAdditionalUserVisible: checked,
            };
            onChange({ isAdditionalUserVisible: checked });
            return newData;
        });
    };

    return (
        <div className="col-12 col-xs-12 col-md-12 col-lg-6 m-auto pb-5">
            {isAddMode && (
                <SSODropdown
                    selectedSSO={data.ssoId}
                    onSSOChange={ssoId => {
                        onSSOSelection(ssoId);
                        onChange({ ...data, ssoId });
                    }}
                    allowNull={true}
                    isRequired={true}
                    inlineLabel={false}
                    label={getText('msg_709a23220f2c')}
                    disabled={isClientSSOReadOnly}
                />
            )}

            <div
                className="form-group mb-3"
                style={{ display: isAddMode ? 'none' : 'block' }}
            >
                <label htmlFor="name">
                    {getText('msg_709a23220f2c')} <span className="text-danger">*</span>
                </label>
                <div className="position-relative">
                    <input
                        type="text"
                        id="name"
                        name="name"
                        className="form-control"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        placeholder={getText('msg_709a23220f2c')}
                        maxLength={20}
                        disabled={true}
                    />
                    <CharacterLimitIndicator
                        inputStr={formData.name}
                        maxChars={20}
                    />
                </div>
            </div>

            <div className="form-group mb-3 position-relative">
                {formData.urlIsLocked && (
                    <div className={`position-absolute ${style.formatTabLock}`}>
                        <i className="bi bi-lock-fill text-muted me-2"></i>
                    </div>
                )}
                <label htmlFor="url">
                    {getText('msg_0e2d9b0777a4')} <span className="text-danger">*</span>
                </label>
                <div className="position-relative">
                    <LoadingWrapper isLoading={isLoadingSSOSelection}>
                        <input
                            type="text"
                            id="url"
                            name="url"
                            className="form-control"
                            value={formData.url}
                            onChange={handleChange}
                            required
                            placeholder={getText('msg_0884c69595a8')}
                            readOnly={formData.urlIsLocked || !data.ssoId}
                            disabled={
                                formData.urlIsLocked ||
                                !data.ssoId ||
                                isClientSSOReadOnly
                            }
                        />
                    </LoadingWrapper>
                </div>
            </div>

            <div className="form-group mb-3">
                <TenantSSOStatusDropdown
                    selectedStatus={formData.tenantSSOStatusId}
                    onStatusChange={handleStatusChange}
                    inlineLabel={false}
                    isRequired={true}
                    label={getText('msg_e6cff3028928')}
                    disabled={!data.ssoId || isClientSSOReadOnly}
                />
            </div>

            {shouldShowSharedWithAdditionalUsers && (
                <div
                    className="checkbox-wrapper mb-3"
                    style={{ marginTop: '0px' }}
                >
                    <input
                        type="checkbox"
                        className="form-check-input custom-checkbox"
                        checked={Boolean(formData.isAdditionalUserVisible)}
                        onChange={handleSharedWithAdditionalUsersChange}
                    />
                    <span
                        style={{
                            color: '#2F2B43',
                            fontWeight: '500',
                            fontSize: '16px',
                        }}
                    >
                        {getText('msg_23c8399f43c0')}
                    </span>
                </div>
            )}
            <div className="d-flex justify-content-between mt-4 pt-4 col-12 m-auto">
                <p>
                    <span className="text-danger">*</span> {getText('msg_7c118f042eaa')}
                </p>
                {((shouldShowSharedWithAdditionalUsers && isClientSSOReadOnly) || !isClientSSOReadOnly) && (
                    <button
                        type="button"
                        className="button-base ing-button-primary-left"
                        onClick={onSave}
                    >
                        {getText('msg_efc007a393f6')}
                    </button>)}
            </div>
        </div>
    );
};

export default SSODetailsTab;
